import custOfetch from '~/composables/useCustomFetch'

export interface CreateSystemEventPayload {
  name: string
  yearNumber: number
  startDate?: string
  endDate?: string
  teamChanges?: boolean
  scoreboard?: boolean
  raffle?: boolean
  auction?: boolean
  registration?: boolean
}

export interface UpdateSystemEventPayload {
  name?: string
  yearNumber?: number
  startDate?: string | null
  endDate?: string | null
  status?: string
  teamChanges?: boolean
  scoreboard?: boolean
  raffle?: boolean
  auction?: boolean
  registration?: boolean
}

export const useSystem = () => {
  const config = useRuntimeConfig()
  const base = `${config.public.apiBase}/system`

  const getEvents = async () => {
    return await custOfetch(`${base}/events`, {
      method: 'GET',
      credentials: 'include',
    }, false) as any[]
  }

  const createEvent = async (payload: CreateSystemEventPayload) => {
    return await custOfetch(`${base}/events`, {
      method: 'POST',
      credentials: 'include',
      body: payload,
    }, false)
  }

  const updateEvent = async (id: string, payload: UpdateSystemEventPayload) => {
    return await custOfetch(`${base}/events/${id}`, {
      method: 'PATCH',
      credentials: 'include',
      body: payload,
    }, false)
  }

  const deleteEvent = async (id: string) => {
    return await custOfetch(`${base}/events/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    }, false)
  }

  const getPrimaryEvent = async () => {
    return await custOfetch(`${config.public.apiBase}/event/primary`, {
      method: 'GET',
      credentials: 'include',
    }, false) as { eventId: string | null }
  }

  const setPrimaryEvent = async (id: string) => {
    return await custOfetch(`${base}/events/${id}/primary`, {
      method: 'PATCH',
      credentials: 'include',
    }, false)
  }

  const getAdministrators = async (eventId: string) => {
    return await custOfetch(`${base}/events/${eventId}/administrators`, {
      method: 'GET',
      credentials: 'include',
    }, false) as any[]
  }

  const assignAdministrator = async (eventId: string, userId: string) => {
    return await custOfetch(`${base}/events/${eventId}/administrators`, {
      method: 'POST',
      credentials: 'include',
      body: { userId },
    }, false)
  }

  const removeAdministrator = async (eventId: string, participantId: string) => {
    return await custOfetch(`${base}/events/${eventId}/administrators/${participantId}`, {
      method: 'DELETE',
      credentials: 'include',
    }, false)
  }

  const getMaintenanceStatus = async () => {
    return await custOfetch(`${base}/maintenance`, {
      method: 'GET',
      credentials: 'include',
    }, false)
  }

  const toggleMaintenance = async () => {
    return await custOfetch(`${base}/maintenance`, {
      method: 'PATCH',
      credentials: 'include',
    }, false)
  }

  const getLegacyWinnerRecordsStatus = async () => {
    return await custOfetch(`${base}/legacy-winner-records`, {
      method: 'GET',
      credentials: 'include',
    }, false)
  }

  const toggleLegacyWinnerRecords = async () => {
    return await custOfetch(`${base}/legacy-winner-records`, {
      method: 'PATCH',
      credentials: 'include',
    }, false)
  }

  const searchUsers = async (query: string) => {
    return await custOfetch(`${base}/users/search?q=${encodeURIComponent(query)}`, {
      method: 'GET',
      credentials: 'include',
    }, false) as any[]
  }

  return {
    getEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    setPrimaryEvent,
    getAdministrators,
    assignAdministrator,
    removeAdministrator,
    getMaintenanceStatus,
    getPrimaryEvent,
    toggleMaintenance,
    getLegacyWinnerRecordsStatus,
    toggleLegacyWinnerRecords,
    searchUsers,
  }
}
